import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, ShieldCheck, ShieldAlert, RefreshCw, CheckCircle2, XCircle, Globe } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProxySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({ tripjack: false, riya: false, logostream: false });
  const [testResults, setTestResults] = useState({ tripjack: null, riya: null, logostream: null });
  const [data, setData] = useState({
    master_enabled: false,
    flags: { tripjack: true, riya: true, logostream: true },
    static_ips: [],
    updated_at: null,
    updated_by: null,
  });

  const axiosCfg = { withCredentials: true };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${BACKEND_URL}/api/admin/settings/proxy`, axiosCfg);
      setData(r.data);
    } catch (e) {
      console.error('Failed to fetch proxy settings', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const saveFlags = async (next) => {
    setSaving(true);
    try {
      const r = await axios.put(
        `${BACKEND_URL}/api/admin/settings/proxy`,
        next,
        axiosCfg,
      );
      if (r.data?.success) setData((prev) => ({ ...prev, flags: r.data.flags }));
    } catch (e) {
      console.error('Failed to save proxy flags', e);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (service) => {
    const next = { ...data.flags, [service]: !data.flags[service] };
    setData((prev) => ({ ...prev, flags: next }));
    // Clear stale test result for the toggled service
    setTestResults((prev) => ({ ...prev, [service]: null }));
    saveFlags(next);
  };

  const testEgress = async (service) => {
    setTesting((prev) => ({ ...prev, [service]: true }));
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/admin/settings/proxy/test?service=${service}`,
        {},
        axiosCfg,
      );
      setTestResults((prev) => ({ ...prev, [service]: r.data }));
    } catch (e) {
      setTestResults((prev) => ({
        ...prev,
        [service]: { success: false, error: e.response?.data?.detail || 'Test failed' },
      }));
    } finally {
      setTesting((prev) => ({ ...prev, [service]: false }));
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="animate-pulse h-24 bg-gray-100 rounded-lg" />
      </div>
    );
  }

  const Row = ({ service, label, description }) => {
    const enabled = !!data.flags[service];
    const masterOff = !data.master_enabled;
    const result = testResults[service];
    return (
      <div className="flex items-start justify-between gap-4 p-4 rounded-lg border border-gray-200 bg-gray-50">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="font-semibold text-gray-900">{label}</p>
            {enabled && !masterOff ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold bg-green-100 text-green-800 rounded">
                <ShieldCheck className="w-3 h-3" /> Proxied
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold bg-gray-200 text-gray-700 rounded">
                <Globe className="w-3 h-3" /> Direct
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600">{description}</p>
          {result && (
            <div
              className={`mt-2 inline-flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-md ${
                result.success
                  ? (result.whitelisted ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-amber-50 text-amber-800 border border-amber-200')
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}
              data-testid={`proxy-test-result-${service}`}
            >
              {result.success ? (
                result.whitelisted ? <CheckCircle2 className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />
              ) : <XCircle className="w-3.5 h-3.5" />}
              <span>
                {result.success
                  ? <>Egress: <code className="font-mono font-bold">{result.public_ip}</code> • {result.using_proxy ? 'via proxy' : 'direct'} • {result.whitelisted ? 'whitelisted' : 'NOT in whitelist'}</>
                  : <>Test failed: {result.error}</>}
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <button
            type="button"
            data-testid={`proxy-toggle-${service}`}
            onClick={() => handleToggle(service)}
            disabled={saving || masterOff}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#659A9B] disabled:opacity-50 ${
              enabled ? 'bg-[#1B375B]' : 'bg-gray-300'
            }`}
            aria-checked={enabled}
            role="switch"
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <button
            type="button"
            data-testid={`proxy-test-btn-${service}`}
            onClick={() => testEgress(service)}
            disabled={testing[service]}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B375B] hover:text-[#0d2037] disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${testing[service] ? 'animate-spin' : ''}`} />
            {testing[service] ? 'Testing...' : 'Test egress IP'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <section
      data-testid="proxy-settings-card"
      className="bg-white rounded-xl border border-gray-200 p-6 mb-6 shadow-sm"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#1B375B]/10 flex items-center justify-center">
            <Shield className="w-5 h-5 text-[#1B375B]" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Outbound Proxy (QuotaGuard)</h2>
            <p className="text-sm text-gray-600">
              Route outbound API calls through whitelisted static IPs
            </p>
          </div>
        </div>
        {!data.master_enabled && (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold bg-red-100 text-red-800 rounded">
            <ShieldAlert className="w-3 h-3" /> Disabled in .env
          </span>
        )}
      </div>

      {data.static_ips?.length > 0 && (
        <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200">
          <p className="text-xs text-blue-900">
            <strong>Whitelisted static IPs:</strong>{' '}
            {data.static_ips.map((ip) => (
              <code
                key={ip}
                className="mx-1 px-1.5 py-0.5 bg-white border border-blue-200 rounded font-mono text-[11px]"
              >
                {ip}
              </code>
            ))}
          </p>
        </div>
      )}

      <div className="space-y-3">
        <Row
          service="tripjack"
          label="Tripjack (Flight Search & Booking)"
          description="Route all Tripjack API calls through the QuotaGuard proxy."
        />
        <Row
          service="riya"
          label="Riya Travel (Flight Search & Booking)"
          description="Route all Riya Travel RWS API calls through the QuotaGuard proxy."
        />
        <Row
          service="logostream"
          label="Logostream (Airline Tail Logos)"
          description="Route airline tail-logo fetches through the QuotaGuard proxy."
        />
      </div>

      {data.updated_at && (
        <p className="mt-4 text-[11px] text-gray-500">
          Last updated: {new Date(data.updated_at).toLocaleString()}
        </p>
      )}
    </section>
  );
};

export default ProxySettings;
